import React from 'react';
import styled from 'styled-components';

import { BaseProps } from '../../types';
import {
  FlexRow,
  SortingDownIcon,
  SortingUpIcon,
  SvgIcon,
} from '@make-software/cspr-design';
import { BodyText } from '../body-text/body-text';
import { Tooltip } from '../tooltip/tooltip';

export enum OrderDirection {
  ASC = 'ASC',
  DESC = 'DESC',
}

export interface SortingProps {
  orderBy?: string | undefined;
  orderDirection?: OrderDirection;
  setOrder?: (orderBy: string | undefined, direction: OrderDirection) => void;
  reverseSortingDirection?: boolean;
}

export interface TableDataHeaderProps extends BaseProps, SortingProps {
  align?: 'left' | 'right' | 'center';
  fitContent?: boolean;
  capitalize?: boolean;
  sortableColumnField?: string | undefined;
  tooltipText?: JSX.Element | string | undefined;
  icon?: JSX.Element | undefined;
  fixedWidthRem?: number;
  filter?: JSX.Element;
}

const StyledTableDataHeader = styled.th<TableDataHeaderProps>(
  ({
    theme,
    align = 'left',
    fitContent,
    fixedWidthRem,
    capitalize = true,
  }) => ({
    textAlign: align,
    height: 20,
    padding: 8,
    ':first-of-type': {
      paddingLeft: 20,
    },
    ':last-of-type': {
      paddingRight: 20,
    },
    ...(fitContent && {
      width: '1%',
    }),
    ...(fixedWidthRem && {
      width: `${fixedWidthRem}rem`,
    }),
    textTransform: capitalize ? 'capitalize' : 'none',
  })
);

const StyledSortedButtons = styled.div(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  marginLeft: '7px',
  '> * + *': {
    marginTop: '2.5px',
  },
}));

const StyledHeaderGroup = styled.div<{ sortable?: boolean }>(
  ({ theme, sortable }) => ({
    display: 'inline-flex',
    flexDirection: 'row',
    alignItems: 'center',
    cursor: sortable ? 'pointer' : 'inherit',
  })
);

const MouseHoverStylesRed = styled.div<{ active?: boolean }>(
  ({ theme, active }) => ({
    lineHeight: '12px',
    path: {
      fill: active
        ? theme.styleguideColors.contentRed
        : theme.styleguideColors.contentTertiary,
    },
    ':hover': {
      path: {
        fill: theme.styleguideColors.fillPrimaryRedHover,
      },
    },
    ':active': {
      path: {
        fill: theme.styleguideColors.fillPrimaryRedClick,
      },
    },
  })
);

export function TableDataHeader({
  children,
  sortableColumnField,
  orderBy,
  orderDirection,
  setOrder,
  tooltipText,
  icon,
  reverseSortingDirection = false,
  filter,
  ...restProps
}: TableDataHeaderProps) {
  const descSortingOrder = reverseSortingDirection
    ? OrderDirection.ASC
    : OrderDirection.DESC;

  const ascSortingOrder = reverseSortingDirection
    ? OrderDirection.DESC
    : OrderDirection.ASC;

  const handleColumnSort = (orderDirectionArg?: OrderDirection) => (ev) => {
    if (sortableColumnField) {
      ev.stopPropagation();
      let direction = orderDirectionArg;
      if (direction == null) {
        if (orderBy !== sortableColumnField) {
          // changed column, set desc
          direction = OrderDirection.DESC;
        } else {
          // same column, switch direction
          direction =
            orderDirection !== OrderDirection.ASC
              ? OrderDirection.ASC
              : OrderDirection.DESC;
        }
      }

      setOrder && setOrder(sortableColumnField, direction);
    }
  };

  return (
    <StyledTableDataHeader scope="col" {...restProps}>
      <StyledHeaderGroup sortable={!!sortableColumnField}>
        <Tooltip tooltipContent={tooltipText} limitWidth>
          <FlexRow gap={3} align={'center'}>
            <BodyText size={1} onClick={handleColumnSort()}>
              {children}
            </BodyText>
            {icon}
          </FlexRow>
        </Tooltip>
        {!!sortableColumnField && (
          <StyledSortedButtons>
            <MouseHoverStylesRed
              active={
                sortableColumnField === orderBy &&
                orderDirection === ascSortingOrder
              }
              onClick={handleColumnSort(ascSortingOrder)}
              tabIndex={0}
              onKeyDown={(ev) => {
                if (ev.key === 'Enter') {
                  handleColumnSort(ascSortingOrder)(ev);
                }
              }}
            >
              <SvgIcon
                height={7}
                src={SortingUpIcon}
                alt={'Sort table header ascending icon'}
                role={'img'}
              />
            </MouseHoverStylesRed>
            <MouseHoverStylesRed
              active={
                sortableColumnField === orderBy &&
                orderDirection === descSortingOrder
              }
              onClick={handleColumnSort(descSortingOrder)}
              tabIndex={0}
              onKeyDown={(ev) => {
                if (ev.key === 'Enter') {
                  handleColumnSort(descSortingOrder)(ev);
                }
              }}
            >
              <SvgIcon
                height={7}
                role={'img'}
                src={SortingDownIcon}
                alt={'Sort table header descending icon'}
              />
            </MouseHoverStylesRed>
          </StyledSortedButtons>
        )}
        {!!filter && <StyledSortedButtons>{filter}</StyledSortedButtons>}
      </StyledHeaderGroup>
    </StyledTableDataHeader>
  );
}

export default TableDataHeader;
